// /src/components/DemoAccessModal.jsx
import React from "react";
import { useTranslation } from "react-i18next";
import API_BASE_URL from "../api";

export default function DemoAccessModal({ open, onClose = () => {} }) {
  const { t, i18n } = useTranslation("common");
  const [email, setEmail] = React.useState("");
  const [submitting, setSubmitting] = React.useState(false);
  const [sent, setSent] = React.useState(false);
  const [error, setError] = React.useState("");
  const [hp, setHp] = React.useState(""); // honeypot
  const modalRef = React.useRef(null);

  const L = {
    header: t("demo.header", "ДЕМО"),
    title: t("demo.title", "Переглянь, як працює Radiomonitor.net у дії!"),
    subtitle: t(
      "demo.subtitle",
      "Отримай доступ до демо-версії та подивись, як система збирає дані на реальних радіостанціях у режимі спостереження."
    ),
    email_label: t("demo.email_label", "Вкажи свій e-mail — ми надішлемо посилання для входу в демо-режим."),
    placeholder: t("demo.placeholder", "you@example.com"),
    send: t("demo.send", "Надіслати запит для входу"),
    sent_title: t("demo.sent_title", "Посилання надіслано!"),
    sent_text: t("demo.sent_text", "Перевірте вашу пошту. Посилання дійсне протягом 48 годин."),
    close: t("common.close", "Закрити"),
    invalid_email: t("demo.invalid_email", "Невірний e-mail"),
    network_error: t("common.network_error", "Помилка мережі. Спробуйте пізніше."),
  };

  // === A11y: фокус в модалці, Esc для закриття, Tab-трап ===
  React.useEffect(() => {
    if (!open) return;
    const root = modalRef.current;
    if (!root) return;

    const prev = document.activeElement;
    const focusables = () =>
      Array.from(
        root.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        )
      ).filter((el) => !el.hasAttribute("disabled") && !el.getAttribute("aria-hidden"));

    const list = focusables();
    if (list.length) list[0].focus();
    else {
      root.setAttribute("tabindex", "-1");
      root.focus();
    }

    const onKey = (e) => {
      if (e.key === "Escape") {
        try { onClose(); } catch {}
      } else if (e.key === "Tab") {
        const els = focusables();
        if (!els.length) return;
        const first = els[0];
        const last = els[els.length - 1];
        const active = document.activeElement;
        if (e.shiftKey) {
          if (active === first || !root.contains(active)) {
            last.focus();
            e.preventDefault();
          }
        } else {
          if (active === last) {
            first.focus();
            e.preventDefault();
          }
        }
      }
    };

    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      if (prev && typeof prev.focus === "function") prev.focus();
    };
  }, [open, onClose]);

  if (!open) return null;

  const validateEmail = (v) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(String(v || "").trim());

  async function submit(e) {
    e?.preventDefault?.();
    setError("");
    if (submitting) return;
    const v = String(email || "").trim();
    if (!validateEmail(v)) {
      setError(L.invalid_email);
      return;
    }
    try {
      setSubmitting(true);
      const API = (API_BASE_URL || "").replace(/\/+$/, "");
      const res = await fetch(API + "/demo-access/request", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          email: v,
          locale: (i18n.language || "uk").split("-")[0],
          _hp: hp || undefined, // honeypot
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const msg = data?.detail || data?.message || L.network_error;
        setError(typeof msg === "string" ? msg : JSON.stringify(msg));
        return;
      }
      // success
      setSent(true);
    } catch (e) {
      setError(L.network_error);
    } finally {
      setSubmitting(false);
    }
  }

  const Header = (
    <div
      style={{
        background: "#057437",
        color: "#fff",
        padding: "20px 28px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        fontWeight: 700,
        fontSize: 22,
        letterSpacing: "0.4px",
        borderTopLeftRadius: 22,
        borderTopRightRadius: 22,
      }}
    >
      <span id="demo-modal-title">{L.header}</span>
      <button
        onClick={onClose}
        type="button"
        aria-label={L.close}
        title={L.close}
        style={{
          background: "none",
          border: "none",
          color: "#fff",
          fontSize: 26,
          cursor: "pointer",
          marginRight: -4,
          marginTop: -4,
          fontWeight: 400,
        }}
      >
        ×
      </button>
    </div>
  );

  return (
    <div
      className="demo-overlay"
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.28)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
        padding: "24px 16px",
      }}
      onClick={onClose}
    >
      <div
        className="demo-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="demo-modal-title"
        aria-describedby="demo-modal-body"
        ref={modalRef}
        style={{
          width: "min(92vw, 520px)",
          background: "#fff",
          borderRadius: 22,
          boxShadow: "0 22px 42px rgba(0,0,0,0.22)",
          overflow: "hidden",
          fontFamily: "Inter, Arial, sans-serif",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {Header}

        <div
          id="demo-modal-body"
          style={{
            padding: "28px 28px 30px",
            color: "#111",
          }}
        >
          {!sent ? (
            <form
              onSubmit={submit}
              style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0 }}
            >
              <h2
                style={{
                  width: "100%",
                  textAlign: "center",
                  margin: "0 0 18px",
                }}
              >
                <span
                  style={{
                    display: "inline-block",
                    maxWidth: 460,
                    fontSize: 19,
                    fontWeight: 700,
                    lineHeight: 1.32,
                    color: "#111",
                  }}
                >
                  {L.title}
                </span>
              </h2>
              <p
                style={{
                  margin: "0 0 16px",
                  fontSize: 15.5,
                  lineHeight: 1.48,
                  maxWidth: 430,
                  textAlign: "left",
                  marginLeft: "auto",
                  marginRight: "auto",
                }}
              >
                {L.subtitle}
              </p>
              <p
                style={{
                  margin: "0 0 24px",
                  fontSize: 15.5,
                  lineHeight: 1.48,
                  maxWidth: 430,
                  textAlign: "left",
                  marginLeft: "auto",
                  marginRight: "auto",
                }}
              >
                {L.email_label}
              </p>

              {/* honeypot */}
              <input
                type="text"
                value={hp}
                onChange={(e) => setHp(e.target.value)}
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                style={{
                  position: "absolute",
                  left: "-9999px",
                  width: 1,
                  height: 1,
                  opacity: 0,
                }}
              />

              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={L.placeholder}
                required
                aria-invalid={!!error && !validateEmail(email)}
                style={{
                  width: "100%",
                  maxWidth: 430,
                  padding: "15px 16px",
                  borderRadius: 10,
                  border: "1px solid #d0d5d2",
                  fontSize: 17,
                  fontFamily: "inherit",
                  marginBottom: 24,
                  background: "#fff",
                  outline: "none",
                  marginLeft: "auto",
                  marginRight: "auto",
                }}
              />

              {error && (
                <div
                  role="alert"
                  style={{
                    color: "#b51f1f",
                    marginTop: -8,
                    marginBottom: 24,
                    fontSize: 14,
                    maxWidth: 380,
                    textAlign: "center",
                  }}
                >
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={submitting}
                style={{
                  width: "100%",
                  maxWidth: 430,
                  background: submitting ? "#318d5a" : "#00723a",
                  color: "#fff",
                  border: "none",
                  padding: "16px 18px",
                  borderRadius: 12,
                  fontSize: 18,
                  fontWeight: 700,
                  cursor: submitting ? "wait" : "pointer",
                  boxShadow: "0 12px 26px rgba(0, 114, 58, 0.25)",
                  transition: "transform 0.2s ease, box-shadow 0.2s ease, background 0.2s ease",
                  marginLeft: "auto",
                  marginRight: "auto",
                }}
              >
                {submitting ? t("common.sending", "Відправляємо…") : L.send}
              </button>
            </form>
          ) : (
            <div>
              <h3
                style={{
                  marginTop: 0,
                  fontSize: 20,
                  fontWeight: 700,
                  color: "#111",
                  textAlign: "center",
                }}
              >
                {L.sent_title}
              </h3>
              <p
                style={{
                  fontSize: 16,
                  lineHeight: 1.5,
                  marginBottom: 24,
                  textAlign: "center",
                  maxWidth: 430,
                  marginLeft: "auto",
                  marginRight: "auto",
                }}
              >
                {L.sent_text}
              </p>
              <div
                style={{
                  width: "100%",
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                <button
                  type="button"
                  onClick={onClose}
                  style={{
                    width: "100%",
                    maxWidth: 430,
                    padding: "14px 16px",
                    fontSize: 16,
                    borderRadius: 10,
                    border: "none",
                    background: "#057437",
                    color: "#fff",
                    fontWeight: 700,
                    cursor: "pointer",
                    boxShadow: "0 8px 22px rgba(5, 116, 55, 0.25)",
                    transition: "transform 0.2s ease, box-shadow 0.2s ease",
                  }}
                >
                  {L.close}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
